import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Badge,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  Pagination,
} from "react-bootstrap";
import { Eye, Search, JournalBookmarkFill } from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
} from "@mui/material";
import axios from "axios";
import API_BASE_URL from "../../../config/api";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{
          color,
          lineHeight: 1,
          opacity: disabled ? 0.4 : 1,
          minWidth: "auto",
        }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

// Fallback static data matching API structure
const staticJournals = {
  success: true,
  count: 3,
  data: [
    {
      _id: "1",
      classId: {
        _id: "1",
        title: "Math 101",
        location: {
          location: "Building A, Room 203",
        },
        Instructor: {
          name: "Prof. Smith",
          email: "prof.smith@example.com",
        },
        status: "Active",
      },
      userId: {
        name: "John Doe",
        email: "john.doe@example.com",
      },
      shareWith: ["Classmates", "TA"],
      title1: "Math Challenge",
      description1: "Struggling with calculus concepts",
      title2: "Today's Feelings",
      description2: "Confused but motivated",
      createdAt: new Date().toISOString(),
    },
    {
      _id: "2",
      classId: {
        _id: "2",
        title: "Science Lab",
        location: {
          location: "Lab 101, Science Wing",
        },
        Instructor: {
          name: "Dr. Johnson",
          email: "dr.johnson@example.com",
        },
        status: "Active",
      },
      userId: {
        name: "Jane Smith",
        email: "jane.smith@example.com",
      },
      shareWith: ["Classmates", "Instructor"],
      title1: "Experiment Results",
      description1: "Successfully completed the experiment",
      title2: "Reflection",
      description2: "Learned a lot about chemical reactions",
      createdAt: new Date().toISOString(),
    },
    {
      _id: "3",
      classId: {
        _id: "3",
        title: "History Class",
        location: {
          location: "Room 105, Main Building",
        },
        Instructor: {
          name: "Prof. Williams",
          email: "prof.williams@example.com",
        },
        status: "Archived",
      },
      userId: {
        name: "Bob Wilson",
        email: "bob.wilson@example.com",
      },
      shareWith: ["Classmates"],
      title1: "Historical Analysis",
      description1: "Research on World War II",
      title2: "Reflection",
      description2: "Fascinating to learn about this period",
      createdAt: new Date().toISOString(),
    },
  ],
};

const ParticipantsJournals = () => {
  const [apiResponse, setApiResponse] = useState(null);
  const [filteredJournals, setFilteredJournals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallbackData, setUsingFallbackData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const fetchJournals = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchTerm.trim()) params.append("search", searchTerm.trim());
        if (classFilter !== "all") params.append("classId", classFilter);
        params.append("page", page);
        params.append("limit", limit);

        const response = await axios.get(
          `${API_BASE_URL}/api/journel/getJournalForAdmin?${params.toString()}`,
        );

        const data = response.data;
        setApiResponse(data);
        setFilteredJournals(data.data || []);
        setUsingFallbackData(false);
        setPagination({
          page: data.currentPage || page,
          limit,
          total: data.totalCount || data.data?.length || 0,
          totalPages:
            data.totalPages ||
            Math.max(
              1,
              Math.ceil((data.totalCount || data.data?.length || 0) / limit),
            ),
        });
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setUsingFallbackData(true);
        setApiResponse(staticJournals);
        setFilteredJournals(staticJournals.data);
        setPagination({
          page: 1,
          limit: pagination.limit,
          total: staticJournals.data.length,
          totalPages: Math.max(
            1,
            Math.ceil(staticJournals.data.length / pagination.limit),
          ),
        });
        setLoading(false);
      }
    },
    [searchTerm, classFilter, pagination.limit],
  );

  useEffect(() => {
    fetchJournals(1, pagination.limit);
  }, [fetchJournals, pagination.limit]);

  // Get unique classes for filter dropdown
  const uniqueClasses = apiResponse
    ? [
        ...new Map(
          apiResponse.data
            .map((journal) => journal?.classId)
            .filter((cls) => cls?._id)
            .map((cls) => [cls._id, cls]),
        ).values(),
      ]
    : [];

  // Apply client-side filters only for fallback data
  useEffect(() => {
    if (!apiResponse) return;
    if (!usingFallbackData) {
      setFilteredJournals(apiResponse.data || []);
      return;
    }

    let result = apiResponse.data;

    if (searchTerm) {
      result = result.filter(
        (journal) =>
          journal?.userId?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          journal?.classId?.title
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          journal?.classId?.Instructor?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    if (classFilter !== "all") {
      result = result.filter((journal) => journal.classId?._id === classFilter);
    }

    setFilteredJournals(result);
  }, [searchTerm, classFilter, apiResponse, usingFallbackData]);

  const handleView = async (journal) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/journel/getByJournalId/${journal?._id}`,
      );
      setSelectedJournal(response?.data?.data);
      setShowModal(true);
      setLoading(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJournal(null);
  };

  const handlePageChange = (page) => {
    fetchJournals(page, pagination.limit);
  };

  const pageStartIndex = (pagination.page - 1) * pagination.limit;
  const visibleJournals = usingFallbackData
    ? filteredJournals.slice(pageStartIndex, pageStartIndex + pagination.limit)
    : filteredJournals;

  console.log("selected Journal", selectedJournal);

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Participants Journals
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <JournalBookmarkFill size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Participants Journals
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {pagination.total || 0} total journals
            </Typography>
          </Box>
        </Box>
        {usingFallbackData && (
          <Badge bg="warning" text="dark" className="px-3 py-2 rounded-pill">
            Using fallback data
          </Badge>
        )}
      </Box>

      {/* Show error message if API failed */}
      {error && (
        <Alert
          variant="warning"
          className="mb-4"
          style={{ borderRadius: "12px" }}
        >
          <Alert.Heading>Connection Issue</Alert.Heading>
          <p className="mb-0">
            Could not connect to the server: {error}. Showing fallback data
            instead.
            <br />
            <small>Some features might be limited.</small>
          </p>
        </Alert>
      )}

      {/* Filters Section */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2.5,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Typography
          sx={{ fontWeight: 600, color: "#1a237e", mb: 2, fontSize: "0.9rem" }}
        >
          Filters
        </Typography>
        <Row className="g-3">
          {/* Search Column */}
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search</Form.Label>
              <Box position="relative">
                <Search
                  size={13}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#aab4cc",
                    zIndex: 1,
                  }}
                />
                <Form.Control
                  style={{ ...fieldStyle, paddingLeft: 30 }}
                  type="text"
                  placeholder="User, class or instructor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Box>
            </Form.Group>
          </Col>

          {/* Class Filter Column */}
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Class</Form.Label>
              <Form.Select
                style={fieldStyle}
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <option value="all">All Classes</option>
                {uniqueClasses.map((cls) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Box>

      {/* Table */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading journals...
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {[
                  "Sr. No",
                  "Class",
                  "Location",
                  "User",
                  "Instructor",
                  "Shared With",
                  "Action",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleJournals.length > 0 ? (
                visibleJournals.map((journal, index) => (
                  <tr
                    key={journal._id || index}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {pageStartIndex + index + 1}
                    </td>
                    <td style={{ fontWeight: 600, color: "#1a237e" }}>
                      {journal?.classId?.title || "N/A"}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {journal?.classId?.location?.location || "N/A"}
                    </td>
                    <td>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                          }}
                        >
                          {journal.userId?.name || "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem", color: "#7b8ab8" }}
                        >
                          {journal.userId?.email || ""}
                        </Typography>
                      </Box>
                    </td>
                    <td>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            color: "#1a237e",
                            display: "block",
                          }}
                        >
                          {journal.classId?.Instructor?.name || "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.7rem", color: "#7b8ab8" }}
                        >
                          {journal.classId?.Instructor?.email || ""}
                        </Typography>
                      </Box>
                    </td>
                    <td>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {Array.isArray(journal.shareWith) &&
                        journal.shareWith.length > 0 ? (
                          journal.shareWith.map((item, i) => (
                            <span
                              key={i}
                              className="badge"
                              style={{
                                background: "#e4eaf8",
                                color: "#1a237e",
                                padding: "4px 8px",
                                borderRadius: "12px",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                              }}
                            >
                              {item}
                            </span>
                          ))
                        ) : (
                          <span
                            className="badge"
                            style={{
                              background: "#f0f4ff",
                              color: "#7b8ab8",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "0.7rem",
                            }}
                          >
                            Not shared
                          </span>
                        )}
                      </Box>
                    </td>
                    <td>
                      <ActionBtn
                        tooltip="View Journal"
                        color="#0d47a1"
                        onClick={() => handleView(journal)}
                      >
                        <Eye size={16} />
                      </ActionBtn>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    No journals found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
        {pagination?.totalPages > 1 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing <b style={{ color: "#1a237e" }}>{pageStartIndex + 1}</b>–
              <b style={{ color: "#1a237e" }}>
                {Math.min(
                  pageStartIndex + visibleJournals.length,
                  pagination.total,
                )}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{pagination.total}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />

              {Array.from(
                { length: Math.min(5, pagination?.totalPages) },
                (_, i) => {
                  let pageNum;
                  if (pagination?.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination?.totalPages - 2) {
                    pageNum = pagination?.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === pagination.page}
                      onClick={() => handlePageChange(pageNum)}
                      style={
                        pageNum === pagination.page
                          ? {
                              backgroundColor: "#1a237e",
                              borderColor: "#1a237e",
                              color: "#fff",
                            }
                          : { border: "none", background: "transparent" }
                      }
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                },
              )}

              <Pagination.Next
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination?.totalPages}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Last
                onClick={() => handlePageChange(pagination?.totalPages)}
                disabled={pagination.page === pagination?.totalPages}
                style={{ border: "none", background: "transparent" }}
              />
            </Pagination>
          </Box>
        )}
      </Box>

      {/* Journal Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <JournalBookmarkFill size={18} /> Journal Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {selectedJournal && (
            <>
              {/* Class Information */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#1a237e",
                    mb: 1.5,
                    fontSize: "0.85rem",
                  }}
                >
                  Class Information
                </Typography>
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #e4eaf8",
                    p: 2,
                  }}
                >
                  <Row>
                    <Col md={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#7b8ab8", display: "block" }}
                      >
                        <strong>Class ID:</strong>{" "}
                        {selectedJournal.classId || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#7b8ab8", display: "block", mt: 1 }}
                      >
                        <strong>Journal ID:</strong>{" "}
                        {selectedJournal._id || "N/A"}
                      </Typography>
                    </Col>
                  </Row>
                </Box>
              </Box>

              {/* Participant Information */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#1a237e",
                    mb: 1.5,
                    fontSize: "0.85rem",
                  }}
                >
                  Participant Information
                </Typography>
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #e4eaf8",
                    p: 2,
                  }}
                >
                  <Row>
                    <Col md={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#7b8ab8", display: "block" }}
                      >
                        <strong>Name:</strong>{" "}
                        {selectedJournal?.userId?.name || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#7b8ab8", display: "block", mt: 1 }}
                      >
                        <strong>Location:</strong>{" "}
                        {selectedJournal?.userId?.location?.location || "N/A"}
                      </Typography>
                    </Col>
                    <Col md={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#7b8ab8", display: "block" }}
                      >
                        <strong>Email:</strong>{" "}
                        {selectedJournal?.userId?.email || "N/A"}
                      </Typography>
                    </Col>
                  </Row>
                </Box>
              </Box>

              {/* Journal Content */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#1a237e",
                    mb: 1.5,
                    fontSize: "0.85rem",
                  }}
                >
                  Journal Content
                </Typography>
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #e4eaf8",
                    p: 2,
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1a237e",
                        fontSize: "0.8rem",
                        mb: 1,
                      }}
                    >
                      {selectedJournal.title1 || "What challenge you today?"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                      {selectedJournal.description1 || "No content available"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1a237e",
                        fontSize: "0.8rem",
                        mb: 1,
                      }}
                    >
                      {selectedJournal.title2 || "How do you feel today?"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                      {selectedJournal.description2 || "No content available"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Sharing Information */}
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#1a237e",
                    mb: 1.5,
                    fontSize: "0.85rem",
                  }}
                >
                  Sharing Information
                </Typography>
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #e4eaf8",
                    p: 2,
                  }}
                >
                  <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                    {Array.isArray(selectedJournal.shareWith) &&
                    selectedJournal.shareWith.length > 0 ? (
                      selectedJournal.shareWith.map((item, i) => (
                        <span
                          key={i}
                          className="badge"
                          style={{
                            background: "#e4eaf8",
                            color: "#1a237e",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                          }}
                        >
                          {item}
                        </span>
                      ))
                    ) : (
                      <span
                        className="badge"
                        style={{
                          background: "#f0f4ff",
                          color: "#7b8ab8",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "0.7rem",
                        }}
                      >
                        Not shared
                      </span>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                    Created on:{" "}
                    {new Date(selectedJournal.createdAt).toLocaleString()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#7b8ab8", display: "block", mt: 1 }}
                  >
                    Last updated:{" "}
                    {new Date(selectedJournal.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={handleCloseModal}
            style={{ borderRadius: "8px" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default ParticipantsJournals;
